/**
 * Nearest ancestor that is its own scrollport, or null when the element sits in
 * the document flow.
 *
 * Resolved by computed `overflow-y` alone, NOT by whether the box currently has
 * anything to scroll: a page whose content happens to be short still owns its
 * scrollport, and treating it as "not a container" is exactly what sends the
 * scroll up to the layout instead.
 */
export const findScrollContainer = (element: HTMLElement): HTMLElement | null => {
  let node = element.parentElement
  while (node) {
    const { overflowY } = window.getComputedStyle(node)
    if (overflowY === 'auto' || overflowY === 'scroll') return node
    node = node.parentElement
  }
  return null
}

/**
 * Scrolls `element` to the top of its own scroll container, keeping `offsetTop`
 * pixels of room above it (for a sticky header that would otherwise cover it).
 *
 * Deliberately NOT `Element.scrollIntoView`: that scrolls EVERY scrollable
 * ancestor, not just the nearest one. The analysis surfaces render inside
 * MainLayout's `.content`, which is a scrollport of its own, so whenever the
 * page's container could not satisfy the request alone — a target near the
 * bottom, or a short page with no scroll range at all — the browser went on to
 * scroll the layout. That slid the whole page up and carried the sticky section
 * tab bar off screen with it, which is why the bar disappeared on some tab
 * clicks and not others.
 *
 * Scrolling the container directly cannot move an ancestor. When the container
 * has no scroll range the call is a no-op, which is the correct outcome: a page
 * short enough not to scroll is already showing the target.
 */
export const scrollWithinContainer = (
  element: HTMLElement,
  offsetTop = 0,
  behavior: ScrollBehavior = 'smooth',
): void => {
  const container = findScrollContainer(element)
  if (!container) {
    // The document is the scroller, so there is no ancestor to drag along.
    const top = window.scrollY + element.getBoundingClientRect().top - offsetTop
    window.scrollTo({ top, behavior })
    return
  }
  const top =
    container.scrollTop +
    element.getBoundingClientRect().top -
    container.getBoundingClientRect().top -
    offsetTop
  container.scrollTo({ top, behavior })
}

/**
 * Marks the header block (controls, banners, section tabs) stuck to the top of a
 * page. Lives here rather than on the component that stamps it because it is a
 * DOM contract the scrollers below read.
 */
export const STICKY_PAGE_HEADER_ATTRIBUTE = 'data-sticky-page-header'

/**
 * Marks a strip that hangs below the sticky header without taking layout room (it overlays the
 * page), so the scrollers land sections below it too.
 */
export const STICKY_HEADER_OVERLAY_ATTRIBUTE = 'data-sticky-header-overlay'

/**
 * Marks the section tab bar — the stuck element on a surface that renders the
 * bar without the header block. Same DOM contract as above.
 */
export const SECTION_TABS_ATTRIBUTE = 'data-section-tabs'

/**
 * Scrolls a `section-<key>` anchor to the top of its scrollport, below whatever
 * is stuck there: the header block, or the bare tab bar where a surface has no
 * block. Measured rather than assumed: both wrap to more rows on a narrow window.
 *
 * Shared by the tab-bar click handler and the hash-landing hook so the two,
 * which can both fire on the same arrival, resolve to the same position.
 */
export const scrollToSectionAnchor = (
  element: HTMLElement,
  behavior: ScrollBehavior = 'smooth',
): void => {
  // Below md the page has no scrollport and the header sticks against the document.
  const scope = findScrollContainer(element) ?? document
  const stuck =
    scope.querySelector<HTMLElement>(`[${STICKY_PAGE_HEADER_ATTRIBUTE}]`) ??
    scope.querySelector<HTMLElement>(`[${SECTION_TABS_ATTRIBUTE}]`)
  const overlays = stuck?.querySelectorAll<HTMLElement>(`[${STICKY_HEADER_OVERLAY_ATTRIBUTE}]`)
  const overlayHeight = Array.from(overlays ?? []).reduce((sum, el) => sum + el.offsetHeight, 0)
  scrollWithinContainer(element, (stuck?.offsetHeight ?? 0) + overlayHeight, behavior)
}

/**
 * Anything that means the reader moved the page themselves — a wheel, a touch
 * drag, a keyboard scroll, a scrollbar drag or a tab click. Shared so the settler
 * (which must let go), the section spy (which may start recording) and the arrival
 * watcher (which must give up) agree.
 */
const READER_INPUT_EVENTS = ['wheel', 'touchmove', 'keydown', 'mousedown'] as const

/** Calls `handler` on every reader input until the returned unsubscribe runs. */
export const onReaderInput = (handler: () => void): (() => void) => {
  const options = { passive: true, capture: true } as const
  READER_INPUT_EVENTS.forEach((event) => window.addEventListener(event, handler, options))
  return () =>
    READER_INPUT_EVENTS.forEach((event) => window.removeEventListener(event, handler, options))
}

/** Long enough for a section's own data to land, short enough not to hold the page. */
const ANCHOR_SETTLE_TIMEOUT_MS = 5_000

let activeSettlers = 0
const settledListeners = new Set<() => void>()

/** True while an arrival anchor is being held, which already re-places the page on each change. */
export const isAnchorSettling = (): boolean => activeSettlers > 0

/** Calls `listener` each time the last held anchor lets go; returns the unsubscribe. */
export const onAnchorSettled = (listener: () => void): (() => void) => {
  settledListeners.add(listener)
  return () => settledListeners.delete(listener)
}

/**
 * Holds an arrival anchor at the top of the scrollport while the page settles.
 *
 * Every section loads its own data after mount, so the page grows under a scroll
 * whose target was computed while it was still short, and the reader lands above
 * where they asked for — refreshing on `#section-TERM_SHEET`, the last section,
 * left them several sections higher. Re-applying on each DOM change follows the
 * target down rather than guessing a delay to wait out.
 *
 * The first scroll animates; the corrections after it snap, because restarting a
 * smooth scroll on every mutation would never visibly arrive. Stops the moment
 * the reader scrolls for themselves, and gives up on a cap so a page that is
 * never quiet cannot hold them.
 */
export const keepAnchoredWhileSettling = (element: HTMLElement): (() => void) => {
  let stopped = false

  const stop = () => {
    if (stopped) return
    stopped = true
    activeSettlers -= 1
    observer.disconnect()
    clearTimeout(giveUpId)
    stopListening()
    if (activeSettlers === 0) settledListeners.forEach((listener) => listener())
  }

  const observer = new MutationObserver(() => {
    if (!stopped) scrollToSectionAnchor(element, 'auto')
  })
  const giveUpId = setTimeout(stop, ANCHOR_SETTLE_TIMEOUT_MS)
  activeSettlers += 1

  scrollToSectionAnchor(element)
  observer.observe(document.body, { childList: true, subtree: true })
  const stopListening = onReaderInput(stop)

  return stop
}
